/**
 * layeredLayout — deterministic columnar layout for small typed diagrams.
 *
 * Single responsibility: place a set of nodes in columns according to the
 * length of the longest predecessor chain reaching each node, with rows
 * assigned in input order. This matches the historical layout used by
 * `ProvenanceGraph` so the React Flow rewrite renders the same shapes
 * for the same inputs — feature parity is structural, not approximated.
 *
 * The algorithm is pure (no DOM, no async), which keeps layout
 * "reproducible from typed input data only" — the acceptance criterion
 * for issue #1580. Heavier layouts (elkjs-driven hierarchical or
 * force-directed) can replace this function without changing the
 * surface; consumers are insulated by `TypedDiagram`.
 *
 * SPDX-License-Identifier: LicenseRef-Proprietary
 * Copyright (c) 2026 Invariant Holdings LLC
 */

import type { TypedDiagramEdge, TypedDiagramNode } from "./TypedDiagram.js";

/**
 * Width × height of a single node, in React Flow's coordinate units.
 *
 * Kept aligned with the SVG dimensions of the legacy `ProvenanceGraph`
 * so that diagrams rewritten on React Flow read at the same visual
 * cadence. Consumers can override the *render size* of an individual
 * node by setting `width` / `height` on the node spec, but column /
 * row spacing is always computed against the layout defaults (or the
 * `LayeredLayoutOptions` overrides). Per-node render-size overrides
 * therefore can overlap their neighbors when they exceed the column
 * width — this trade-off is intentional: the legacy `ProvenanceGraph`
 * uses the same single-grid model, and the small DAGs the CSK demos
 * render don't yet warrant per-column-max layout. Larger graphs
 * should switch to an asynchronous layout engine (e.g., elkjs).
 */
export const DEFAULT_NODE_WIDTH = 140;
export const DEFAULT_NODE_HEIGHT = 48;

/**
 * Per-axis spacing between columns / rows in the deterministic layout.
 *
 * Tuned for the small DAGs (≤ ~20 nodes) that CSK demos render. Larger
 * graphs should switch to an asynchronous layout engine.
 */
export const DEFAULT_COLUMN_GAP = 60;
export const DEFAULT_ROW_GAP = 24;

export interface PositionedNode<TKind extends string> {
  readonly id: string;
  readonly kind: TKind;
  readonly label: string;
  readonly x: number;
  readonly y: number;
  readonly width: number;
  readonly height: number;
}

/**
 * Optional knobs for callers that need wider columns or taller rows.
 *
 * The defaults match the legacy `ProvenanceGraph` so the migrated demo
 * needs no per-axis tuning.
 */
export interface LayeredLayoutOptions {
  readonly nodeWidth?: number;
  readonly nodeHeight?: number;
  readonly columnGap?: number;
  readonly rowGap?: number;
}

/**
 * Compute deterministic column/row coordinates for `nodes`.
 *
 * Edges define predecessors: each node is placed in the column whose
 * index is `1 + max(predecessor depths)`. Within a column, nodes are
 * ordered by their position in `nodes`. The function is referentially
 * transparent — identical inputs produce identical outputs, making it
 * safe to snapshot.
 */
export function layeredLayout<TKind extends string>(
  nodes: readonly TypedDiagramNode<TKind>[],
  edges: readonly TypedDiagramEdge[],
  options: LayeredLayoutOptions = {},
): readonly PositionedNode<TKind>[] {
  const nodeWidth = options.nodeWidth ?? DEFAULT_NODE_WIDTH;
  const nodeHeight = options.nodeHeight ?? DEFAULT_NODE_HEIGHT;
  const columnGap = options.columnGap ?? DEFAULT_COLUMN_GAP;
  const rowGap = options.rowGap ?? DEFAULT_ROW_GAP;

  const knownIds = new Set<string>();
  const incoming = new Map<string, string[]>();
  for (const node of nodes) {
    knownIds.add(node.id);
    incoming.set(node.id, []);
  }
  // Edges with dangling endpoints contribute no layout signal; they are
  // also dropped at render time, so the layout and rendering agree.
  for (const edge of edges) {
    if (!knownIds.has(edge.source) || !knownIds.has(edge.target)) continue;
    const preds = incoming.get(edge.target);
    if (preds !== undefined) preds.push(edge.source);
  }

  const depthCache = new Map<string, number>();
  function depth(id: string, seen: Set<string>): number {
    const cached = depthCache.get(id);
    if (cached !== undefined) return cached;
    // Cycle guard — if we re-enter the same id while walking, treat it
    // as a root for the purpose of this recursion. The diagram is then
    // still laid out, just without infinite descent.
    if (seen.has(id)) return 0;
    const preds = incoming.get(id) ?? [];
    if (preds.length === 0) {
      depthCache.set(id, 0);
      return 0;
    }
    seen.add(id);
    let maxPredDepth = 0;
    for (const pred of preds) {
      maxPredDepth = Math.max(maxPredDepth, depth(pred, seen));
    }
    seen.delete(id);
    const d = 1 + maxPredDepth;
    depthCache.set(id, d);
    return d;
  }
  for (const node of nodes) depth(node.id, new Set());

  const rowCursor = new Map<number, number>();
  return nodes.map((node) => {
    const column = depthCache.get(node.id) ?? 0;
    const rowIndex = rowCursor.get(column) ?? 0;
    rowCursor.set(column, rowIndex + 1);
    return {
      id: node.id,
      kind: node.kind,
      label: node.label,
      width: node.width ?? nodeWidth,
      height: node.height ?? nodeHeight,
      x: column * (nodeWidth + columnGap),
      y: rowIndex * (nodeHeight + rowGap),
    };
  });
}
